import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./TableView.css";
import axios from "axios";
import API_BASE_URL from "../../config/env";

interface LeadItem {
  id: number;
  raw_lead_id: number | null;
  name: string;
  email: string | null;
  is_valid: number;
  raw_lead_created_at: string | null;
  voice_note: string | null;
  raw_leads_budget_max: string | null;
  raw_leads_budget_min: string | null;
  raw_leads_configuration: string | null;
  raw_leads_property_type: string | null;
  raw_leads_buying_location: string | null;
  customer_address: string | null;
  raw_leads_remarks: string | null;
  phone_number?: string;
  status?: string;
  lead_id?: number | string;
}

const TableView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState<keyof LeadItem>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filterType, setFilterType] = useState<string | null>(null);

  const [tableData, setTableData] = useState<LeadItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Get filter type from location state
  useEffect(() => {
    if (location.state?.filterType) {
      setFilterType(location.state.filterType);
    } else {
      setFilterType(null);
    }
    setCurrentPage(1);
  }, [location.state]);

  const getEndpoint = () => {
    switch (filterType) {
      case "active_count":
        return `${API_BASE_URL}/api/global-partner-connect/leads/active`;
      case "invalid_count":
        return `${API_BASE_URL}/api/global-partner-connect/leads/invalid`;
      case "not_qualify":
        return `${API_BASE_URL}/api/global-partner-connect/leads/not-qualified`;
      case "siteVisit":
        return `${API_BASE_URL}/api/global-partner-connect/leads/site-visit`;
      case "f2f":
        return `${API_BASE_URL}/api/global-partner-connect/leads/f2f-meeting`;
      case "lead_scheduled":
        return `${API_BASE_URL}/api/global-partner-connect/leads/token-done`;
      case "transaction_done":
        return `${API_BASE_URL}/api/global-partner-connect/leads/transaction-done`;
      case "total_count":
        return `${API_BASE_URL}/api/global-partner-connect/leads/total-leads`;
      default:
        return `${API_BASE_URL}/api/global-partner-connect/leads/active`;
    }
  };

  const getPageTitle = () => {
    switch (filterType) {
      case "active_count":
        return "Active Leads";
      case "invalid_count":
        return "Invalid Leads";
      case "not_qualify":
        return "Not Qualified Leads";
      case "siteVisit":
        return "Site Visit Leads";
      case "f2f":
        return "F2F Meeting Leads";
      case "lead_scheduled":
        return "Lead Scheduled";
      case "transaction_done":
        return "Transaction Done Leads";
      case "total_count":
        return "Total Leads";
      default:
        return "Active Leads";
    }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("authToken");

      if (!token) {
        setError("Authentication token not found. Please login again.");
        setLoading(false);
        return;
      }

      const endpoint = getEndpoint();

      console.log("Fetching from endpoint:", endpoint); // Debug log

      const response = await axios.get(endpoint, {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search: search || undefined,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = response.data;

      // Handle different response structures
      if (data?.status === "success") {
        const inner = data.data ?? data;
        const items = inner.items ?? inner.data ?? [];
        
        const processedItems = items.map((item: any) => ({
          ...item,
          lead_id: item.lead_id || item.id || item.raw_lead_id,
          id: item.id || item.lead_id
        }));
        
        setTableData(processedItems);
        setTotalPages(inner.totalPages || inner.last_page || 1);
        setTotalLeads(inner.total || inner.total_count || 0);
      } else if (data?.items) {
        const processedItems = data.items.map((item: any) => ({
          ...item,
          lead_id: item.lead_id || item.id || item.raw_lead_id,
          id: item.id || item.lead_id
        }));
        setTableData(processedItems);
        setTotalPages(data.totalPages || data.last_page || 1);
        setTotalLeads(data.total || data.total_count || 0);
      } else if (Array.isArray(data?.data)) {
        const processedItems = data.data.map((item: any) => ({
          ...item,
          lead_id: item.lead_id || item.id || item.raw_lead_id,
          id: item.id || item.lead_id
        }));
        setTableData(processedItems);
        setTotalPages(data.totalPages || data.last_page || 1);
        setTotalLeads(data.total || data.total_count || processedItems.length);
      } else if (Array.isArray(data)) {
        const processedItems = data.map((item: any) => ({
          ...item,
          lead_id: item.lead_id || item.id || item.raw_lead_id,
          id: item.id || item.lead_id
        }));
        setTableData(processedItems);
        setTotalPages(1);
        setTotalLeads(data.length);
      } else {
        console.warn("Unexpected response structure:", data);
        setTableData([]);
        setTotalLeads(0);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error("Error fetching leads:", err);
      console.error("Error response:", err.response);

      if (err.response?.status === 404) {
        setError(
          `API endpoint not found: ${getEndpoint()}. Please check if the API route is correct.`
        );
      } else if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
        // Optionally redirect to login
        // navigate("/");
      } else if (err.response?.status === 500) {
        setError("Server error. Please try again later.");
      } else if (err.code === 'ERR_NETWORK') {
        setError("Network error. Please check your internet connection.");
      } else {
        setError(
          err.response?.data?.message || err.message || "Failed to fetch leads"
        );
      }

      setTableData([]);
      setTotalLeads(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever dependencies change
  useEffect(() => {
    fetchLeads();
  }, [currentPage, rowsPerPage, search, filterType]);

  // Client-side sort
  const sortedData = useMemo(() => {
    if (!tableData.length) return [];
    const sorted = [...tableData];
    sorted.sort((a, b) => {
      let aVal: any = a[sortColumn] ?? "";
      let bVal: any = b[sortColumn] ?? "";
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tableData, sortColumn, sortDirection]);

  const handleSort = (column: keyof LeadItem) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: keyof LeadItem) => {
    if (sortColumn !== column) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const clearFilter = () => {
    setFilterType(null);
    navigate("/leads", { state: {} });
    setCurrentPage(1);
  };

  const handleLeadClick = (lead: LeadItem) => {
    const leadId = lead.lead_id || lead.id || lead.raw_lead_id;
    navigate(`/lead/${leadId}`);
  };

  const formatBudget = (min: string | null, max: string | null) => {
    if (min && max)
      return `₹${Number(min).toLocaleString()} - ₹${Number(max).toLocaleString()}`;
    if (min) return `₹${Number(min).toLocaleString()} +`;
    if (max) return `Up to ₹${Number(max).toLocaleString()}`;
    return "N/A";
  };

  const getStatusBadge = (isValid: number) => {
    switch (isValid) {
      case 1:
        return <span className="status-badge status-valid">Valid</span>;
      case 0:
        return <span className="status-badge status-invalid">Invalid</span>;
      default:
        return <span className="status-badge status-pending">Pending</span>;
    }
  };

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePrev = () => canGoPrev && setCurrentPage((p) => p - 1);
  const handleNext = () => canGoNext && setCurrentPage((p) => p + 1);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | "...")[] = [1];
    if (currentPage > 3) pages.push("...");
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  if (error) {
    return (
      <div className="leadTablePage">
        <div className="error-container">
          <div className="error-message">
            <h3>Error Loading Leads</h3>
            <p>{error}</p>
            <div className="error-actions">
              <button onClick={() => fetchLeads()} className="retry-btn">
                Retry
              </button>
              <button onClick={() => navigate("/dashboard")} className="back-btn">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="leadTablePage">
      <div className="leadTableContainer">
        <div className="leadTableHeader">
          <div className="headerTitle">
            <h2>{getPageTitle()}</h2>
            {filterType && (
              <button onClick={clearFilter} className="clear-filter-btn-header">
                Clear Filter ✕
              </button>
            )}
          </div>

          <div className="headerControls">
            <input
              type="text"
              placeholder="🔍 Search leads by name, email, or phone..."
              className="searchInput"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className="tableWrapper">
          <table className="leadTable">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")} className="sortable">
                  # {getSortIcon("id")}
                </th>
                <th onClick={() => handleSort("lead_id")} className="sortable">
                  Lead ID {getSortIcon("lead_id")}
                </th>
                <th onClick={() => handleSort("name")} className="sortable">
                  Name {getSortIcon("name")}
                </th>
                <th>Configuration</th>
                <th>Buying Location</th>
                <th>Budget</th>
                {/* <th>Address</th> */}
                <th>Remarks</th>
                <th>Voice Note</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="noData">
                    <div className="loader-container">
                      <div className="loader"></div>
                      <p>Loading leads...</p>
                    </div>
                  </td>
                </tr>
              ) : sortedData.length > 0 ? (
                sortedData.map((item, index) => {
                  const displayLeadId = item.lead_id || item.id || item.raw_lead_id || "N/A";
                  
                  return (
                    <tr
                      key={item.id || index}
                      onClick={() => handleLeadClick(item)}
                      className="clickable-row"
                    >
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td className="leadIdCell">
                        <strong>{displayLeadId}</strong>
                      </td>
                      <td className="nameCell">
                        <strong>{item.name || "N/A"}</strong>
                      </td>
                      <td>{item.raw_leads_configuration ?? "N/A"}</td>
                      <td>{item.raw_leads_buying_location ?? "N/A"}</td>
                      <td className="budget-cell">
                        {formatBudget(item.raw_leads_budget_min, item.raw_leads_budget_max)}
                      </td>
                      <td className="remarks-cell" title={item.raw_leads_remarks ?? ""}>
                        {item.raw_leads_remarks
                          ? item.raw_leads_remarks.length > 40
                            ? `${item.raw_leads_remarks.substring(0, 40)}…`
                            : item.raw_leads_remarks
                          : "N/A"}
                      </td>
                      <td>
                        {item.voice_note ? (
                          <audio
                            controls
                            className="audioPlayer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <source src={item.voice_note} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>{getStatusBadge(item.is_valid)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10} className="noData">
                    <div className="no-data-message">
                      <p>No leads found</p>
                      {search && <p>Try adjusting your search criteria</p>}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalLeads > 0 && (
          <div className="tableFooter">
            <div className="rowsPerPage">
              <span>Show</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>entries</span>
            </div>

            <div className="pageInfo">
              Showing{" "}
              {Math.min((currentPage - 1) * rowsPerPage + 1, totalLeads)}–
              {Math.min(currentPage * rowsPerPage, totalLeads)} of {totalLeads}
            </div>

            <div className="pagination">
              <button
                onClick={handlePrev}
                disabled={!canGoPrev}
                className="pagination-btn"
              >
                Previous
              </button>

              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="pagination-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p as number)}
                    className={`pagination-btn${currentPage === p ? " active" : ""}`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={handleNext}
                disabled={!canGoNext}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableView;